import { createFileRoute, Link } from "@tanstack/react-router";
import { seo } from "@/lib/seo";
import {
  CheckList,
  ClosingCta,
  MarketingPage,
  MarketingSection,
  StartFreeButton,
} from "@/components/marketing/marketing-kit";

export const Route = createFileRoute("/revision-tracker")({
  head: () =>
    seo({
      title: "JEE Revision Planner — Spaced Repetition Tracker | The Quantum Mind",
      description:
        "Free JEE revision planner using spaced repetition. Every lecture you complete is scheduled for repeat revision, with missed revisions carried forward automatically.",
      path: "/revision-tracker",
    }),
  component: RevisionTrackerLanding,
});

function RevisionTrackerLanding() {
  return (
    <MarketingPage>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        JEE revision planner
      </p>
      <h1 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl">
        Revision that schedules itself
      </h1>
      <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground">
        Tick a lecture the day you finish it and the revision planner queues that lecture for spaced
        revision — short intervals first, longer ones later. Each morning you get one honest list of
        what is due, and anything you skip is carried forward instead of being lost.
      </p>
      <div className="mt-6 flex flex-wrap gap-3">
        <StartFreeButton label="Start your revision plan" />
        <Link
          to="/revisions"
          className="glass inline-flex items-center justify-center rounded-full border border-hairline px-6 py-3 text-sm font-semibold transition-transform active:scale-95"
        >
          Open the revision planner
        </Link>
      </div>

      <MarketingSection title="How it works">
        <CheckList
          items={[
            "Add a chapter with its number of lectures",
            "Mark each lecture complete on the day you finish it",
            "Spaced revisions are generated automatically for that lecture",
            "Clear today's revision list before starting new lectures",
            "Missed revisions become backlog you can clear later",
            "Chapter-wise progress shows what is genuinely retained",
          ]}
        />
      </MarketingSection>

      <MarketingSection title="Why spaced repetition">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Understanding a lecture once is not the same as remembering it in an exam hall months
          later. Revisiting the same material at increasing intervals is the cheapest way to keep
          your syllabus alive — the planner just makes sure the schedule actually happens.
        </p>
      </MarketingSection>

      <ClosingCta
        title="Keep every chapter alive till the exam"
        body="Create a free account to save your chapters and revision schedule."
      />
    </MarketingPage>
  );
}
